import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";
import "./ColourPicker.css";
import { useTranslation } from "react-i18next";

export default function ColorPickerField({
  value = "#ffffff",
  onChange,
  closeOnSelect = true,
  disabled = false,
  label = "Color",
  livePreview = true,
  applyOnDrag = true,       // NEW: update parent while dragging
}) {
  const { t } = useTranslation('global');
  const [open, setOpen] = useState(false);
  const [tempColor, setTempColor] = useState(value);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: "bottom" });
  const triggerRef = useRef(null);
      const handleTempChange = (next) => {
    setTempColor(next);
    if (applyOnDrag) onChange?.(next);   // push up immediately
  };
  // keep internal temp in sync when value changes and picker closed
  useEffect(() => {
    if (!open) setTempColor(value);
  }, [value, open]);

  // position popover in viewport (fixed)
  const positionPopover = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const popW = 300;   // match width in CSS
    const popH = 250;   // approx height (picker + controls)
    const gap = 8;

    let left = r.left;
    // prevent overflow right
    if (left + popW > window.innerWidth - 8) left = Math.max(8, window.innerWidth - popW - 8);

    // prefer bottom; flip to top if not enough space
    let placement = "bottom";
    let top = r.bottom + gap;
    if (top + popH > window.innerHeight - 8) {
      placement = "top";
      top = r.top - gap - popH;
      if (top < 8) { // if still tight, clamp
        top = Math.max(8, r.bottom + gap);
        placement = "bottom";
      }
    }

    setCoords({ top, left, placement });
  };

  useEffect(() => {
    if (open) {
      positionPopover();
      const onScrollOrResize = () => positionPopover();
      window.addEventListener("scroll", onScrollOrResize, true);
      window.addEventListener("resize", onScrollOrResize);
      return () => {
        window.removeEventListener("scroll", onScrollOrResize, true);
        window.removeEventListener("resize", onScrollOrResize);
      };
    }
  }, [open]);

  // close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const handleDown = (e) => {
      const trigger = triggerRef.current;
      // if click NOT on trigger OR in our portal popover, close
      // The popover itself handles pointer events; we only guard trigger here.
      if (trigger && !trigger.contains(e.target)) {
        // let portal clicks through: close only if target is not inside a .cp-popover
        const pop = (e.target instanceof Element) && e.target.closest(".cp-popover");
        if (!pop) {
          setOpen(false);
          setTempColor(value);
        }
      }
    };

    const handleEsc = (e) => e.key === "Escape" && (setOpen(false), setTempColor(value));
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open, value]);

  const commit = () => {
    onChange?.(tempColor);
    if (closeOnSelect) setOpen(false);
  };

  const cancel = () => {
    setTempColor(value);
    setOpen(false);
  };

  const displayColor = livePreview && open ? tempColor : value;

  return (
    <div className={`cp-field ${disabled ? "is-disabled" : ""}`}>
      {label && <label className="cp-label">{label}</label>}

          <button
        ref={triggerRef}
        type="button"
        className="cp-swatch-btn"
        onClick={() => !disabled && setOpen(v => !v)}
        aria-label="Choose color"
        disabled={disabled}
      >
        <span className="cp-swatch" style={{ backgroundColor: displayColor }} />
        <span className="cp-code">{displayColor.toUpperCase()}</span>
        <svg className={`cp-caret ${open ? "open" : ""}`} viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d="M7 10l5 5 5-5H7z" fill="currentColor" />
        </svg>
      </button>

      {open && createPortal(
        <div className={`cp-popover cp-popover--${coords.placement}`} style={{ top: coords.top, left: coords.left }}>
          <div className="cp-body">
            <HexColorPicker color={tempColor} onChange={handleTempChange} />
            {/* <div className="cp-input-wrapper">
              <input
                className="cp-input"
                value={tempColor}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  const normalized = v.startsWith("#") ? v : `#${v.replace(/^#/, "")}`;
                  handleTempChange(normalized);     // also live-update from text
                }}
                spellCheck={false}
                aria-label="Hex color"
              />
            </div> */}

            <div className="cp-actions">
              <button className="cp-btn cp-btn--ghost" type="button" onClick={cancel}>{t("close")}</button>
              {/* <button className="cp-btn cp-btn--primary" type="button" onClick={commit}>Select</button> */}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

